import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";
import { Bell, Check, Mail } from "lucide-react";

const EVENT_META = {
  supplier_signup:            { label: "New supplier signup",  colour: "#264839" },
  paid_subscription:          { label: "New paid subscription", colour: "#3E7A5A" },
  payment_failed:             { label: "Payment failed",       colour: "#B35C4A" },
  plan_change:                { label: "Plan changed",         colour: "#4A6B9B" },
  subscription_cancelled:     { label: "Subscription cancelled", colour: "#7D3A2B" },
  new_rfq:                    { label: "New RFQ",              colour: "#264839" },
  stalled_onboarding:         { label: "Stalled onboarding",   colour: "#B39439" },
  founding_featured_expiring: { label: "Founding placement expiring", colour: "#7D8C85" },
  verification_request:       { label: "Verification request",  colour: "#3E7A5A" },
  email_safe_mode_production: { label: "⚠ Email safe mode ON in production", colour: "#B35C4A" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60)      return "just now";
  if (diff < 3600)    return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)   return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800)  return `${Math.floor(diff / 86400)}d ago`;
  return dt.toISOString().split("T")[0];
}

export default function NotificationCentre() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [digestLast, setDigestLast] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const loadDigestStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications/digest/status");
      setDigestLast(data.last || null);
    } catch (_) { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (unreadOnly) params.set("unread_only", "true");
      if (filter)     params.set("event_type", filter);
      const { data } = await api.get(`/admin/notifications?${params}`);
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      setItems([]);
      setLoadError(describeLoadError(e, "notifications"));
      toast.error(e.response?.data?.detail || "Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, [filter, unreadOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDigestStatus(); }, [loadDigestStatus]);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      // Force=true so admins can preview the digest even when the 24h window is empty.
      const { data } = await api.post("/admin/notifications/digest/run-now?force=true");
      if (data.status === "sent") {
        toast.success(`Digest sent (${data.total} event${data.total === 1 ? "" : "s"}) — check hello@accommodationsuppliers.com`);
      } else if (data.status === "skipped_empty") {
        toast.info("No overnight activity — digest skipped");
      } else if (data.status === "send_failed") {
        toast.error("Digest was queued but Resend returned an error — see backend logs");
      } else {
        toast.info(`Digest ${data.status}`);
      }
      loadDigestStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const markOne = async (id) => {
    try {
      await api.post("/admin/notifications/read", { ids: [id] });
      setItems((xs) => xs.map((x) => x.id === id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error("Could not mark as read");
    }
  };

  const markAll = async () => {
    try {
      const { data } = await api.post("/admin/notifications/read", {});
      const now = new Date().toISOString();
      setItems((xs) => xs.map((x) => x.read_at ? x : { ...x, read_at: now }));
      setUnread(0);
      toast.success(`Marked ${data.marked} as read`);
    } catch (e) {
      toast.error("Could not mark all as read");
    }
  };

  const eventTypes = Object.keys(EVENT_META);

  return (
    <div data-testid="admin-notification-centre">
      {/* Iter63 · Morning digest banner + Send Now */}
      <div className="mb-5 border border-[#E8ECE9] bg-[#FAFAF7] p-4 flex flex-wrap items-center gap-3" data-testid="morning-digest-banner">
        <Mail className="w-4 h-4 text-[#264839]" />
        <div className="flex-1 min-w-[240px]">
          <div className="tracking-overline text-[10px] text-[#264839]">Daily morning digest</div>
          <div className="text-sm text-[#11211A] mt-0.5">
            Auto-emailed to <strong>hello@accommodationsuppliers.com</strong> every day at 8:00 AEST.
          </div>
          <div className="text-[11px] text-[#7D8C85] mt-0.5" data-testid="digest-last-status">
            {digestLast
              ? `Last run · ${(digestLast.sent_at || "").slice(0, 16).replace("T"," ")} UTC · ${digestLast.total} event${digestLast.total === 1 ? "" : "s"} · ${digestLast.resend_ok ? "delivered" : "send failed"}`
              : "No digest sent yet."}
          </div>
        </div>
        <button
          type="button"
          onClick={sendDigestNow}
          disabled={sendingDigest}
          className="text-xs uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-3 py-1.5 hover:bg-[#264839] hover:text-white transition disabled:opacity-40"
          data-testid="digest-send-now-btn"
        >
          {sendingDigest ? "Sending…" : "Send now"}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="tracking-overline text-[10px] text-[#264839] mr-auto flex items-center gap-2">
          <Bell className="w-3.5 h-3.5" />
          Live platform events
          {unread > 0 && (
            <span className="bg-[#B35C4A] text-white text-[10px] px-1.5 py-0.5 rounded-full tabular-nums" data-testid="notif-unread-badge-inline">
              {unread} unread
            </span>
          )}
        </div>
        <label className="text-xs text-[#4A5550] flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            data-testid="notif-unread-toggle"
          />
          Unread only
        </label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-2 py-1.5 text-xs border border-[#C4CFC9] bg-white"
          data-testid="notif-type-filter"
        >
          <option value="">All events</option>
          {eventTypes.map((t) => <option key={t} value={t}>{EVENT_META[t].label}</option>)}
        </select>
        <button
          type="button"
          onClick={markAll}
          disabled={unread === 0}
          className="text-xs uppercase tracking-widest font-semibold border border-[#264839] text-[#264839] px-3 py-1.5 hover:bg-[#264839] hover:text-white transition disabled:opacity-40"
          data-testid="notif-mark-all-read"
        >
          Mark all read
        </button>
      </div>

      {loading && <div className="text-sm text-[#7D8C85]">Loading…</div>}
      <LoadErrorBanner error={loadError} onRetry={load} testId="notif-error" />
      {!loading && !loadError && items.length === 0 && (
        <div className="border border-dashed border-[#E8ECE9] p-8 text-center text-sm text-[#7D8C85]" data-testid="notif-empty-state">
          Nothing new yet. Events will appear here as they happen.
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="divide-y divide-[#E8ECE9] border border-[#E8ECE9]" data-testid="notif-list">
          {items.map((n) => {
            const meta = EVENT_META[n.event_type] || { label: n.event_type, colour: "#7D8C85" };
            return (
              <li
                key={n.id}
                onClick={() => n.read_at ? null : markOne(n.id)}
                className={`p-4 flex gap-4 items-start ${n.read_at ? "bg-white" : "bg-[#FAFAF7] cursor-pointer hover:bg-[#F0EFEA]"}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="w-1 self-stretch" style={{ background: meta.colour }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="tracking-overline text-[10px]" style={{ color: meta.colour }}>{meta.label}</span>
                    <span className="text-[11px] text-[#7D8C85]">· {relativeTime(n.created_at)}</span>
                    {!n.read_at && <span className="w-1.5 h-1.5 rounded-full bg-[#B35C4A]" title="Unread" />}
                  </div>
                  <div className="text-sm text-[#11211A] mt-1">{n.subject}</div>
                  {n.meta && Object.keys(n.meta).length > 0 && (
                    <div className="text-[11px] text-[#7D8C85] mt-1 space-x-3">
                      {n.meta.email    && <span>{n.meta.email}</span>}
                      {n.meta.tier     && <span>· {n.meta.tier}</span>}
                      {n.meta.reason   && <span>· Reason: {n.meta.reason}</span>}
                      {n.meta.percent !== undefined && <span>· {n.meta.percent}% complete</span>}
                      {n.meta.expires_at && <span>· expires {(n.meta.expires_at + "").split("T")[0]}</span>}
                    </div>
                  )}
                </div>
                {n.read_at && <Check className="w-3.5 h-3.5 text-[#7D8C85] flex-shrink-0 mt-1" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
